import React, { useMemo, useState, useEffect, useCallback } from "react";
import "./Info.css";

function Info() {
  // Make this page background pure black only while mounted
  useEffect(() => {
    document.body.classList.add("page--black");
    return () => document.body.classList.remove("page--black");
  }, []);

  const team = useMemo(
    () => [
      {
        name: "Sahil Gavhane",
        role: "Cinematographer",
        headshot: "/assets/team/sahil.JPG",
        about: [
          "I am a freelance cinematographer and filmmaker based in Pune, Maharashtra, India. I hold a Diploma in Filmmaking and a Bachelor’s Degree in Cinematography, and I have been working in the film and media industry for over 5 years.",
          "I believe every frame holds a story, and I strive to craft visuals that communicate emotion, depth, and authenticity. My work is shaped by curiosity and the urge to experiment, leading me to explore a wide spectrum of formats and styles—feature films, short films, documentaries, promotional films, music videos, and creative experimental projects.",
          "As a cinematographer, I operate confidently across multiple professional camera systems, including RED, Sony, Panasonic, Canon, and ARRI. I also have hands-on experience with various filmmaking tools such as gimbal rigs (DJI Ronin), Steadicam setups, and motion-control systems for dynamic shots and time-lapses.",
          "I have contributed to both Hindi feature films and Marathi feature films, bringing a blend of technical precision and artistic vision to every project I am part of.",
        ],
        phone: "+91 7820938152",
        email: "gavhane.sahil11@gmail.com",
        links: [{ label: "Instagram", url: "https://www.instagram.com/" }],
      },
      {
        name: "Bhavya Ghasiya",
        role: "Cinematographer",
        headshot: "/assets/team/bhavya.jpg",
        about: [
          "I am a cinematographer with experience across feature films, short films, music videos, and commercial projects. My work is rooted in thoughtful visual storytelling, with careful attention to light, composition, and movement. I approach each project by developing a visual language that serves the narrative and respects the director’s vision. Across formats and scales, I value clarity, collaboration, and images that feel considered and enduring.",
        ],
        phone: "+91 98XXXXXXXX",
        email: "aarya.dp@example.com",
        links: [{ label: "Instagram", url: "https://www.instagram.com/" }],
      },
      {
        name: "Akhil Patel",
        role: "Photographer",
        headshot: "/assets/team/akhil.JPEG",
        about: [
          "I’m Akhil Patel, a commercial photographer with over 3 years of professional experience, specializing in creating visually compelling imagery that blends strong composition, fine detail, and meaningful storytelling.",
          "My work span commercial, food and products ,lifestyle, editorial, and architectural photography, with a strong emphasis on architecture and interior spaces, where design, light, and structure come together.",
          "I believe great photography is not just about aesthetics—it’s about emotion, balance, and purpose. In architectural and interior photography, this means carefully observing light, scale, materials, textures, and spatial flow to create images that feel refined and intentional. This approach earned me the Best Composition Trophy, recognizing my ability to produce visually harmonious frames with artistic clarity.",
          "From concept planning to the final edit, I manage the entire photography production process, working closely with architects, interior designers, and brands to ensure each project reflects the original design vision while maintaining a distinctive creative edge. I’m passionate about transforming thoughtfully designed spaces into timeless visuals that connect, communicate, and leave a lasting impression.",
        ],
        phone: "+91 90XXXXXXXX",
        email: "riya.photo@example.com",
        links: [{ label: "Instagram", url: "https://www.instagram.com/" }],
      },
    ],
    []
  );

  const [activeIdx, setActiveIdx] = useState(0);
  const active = team[activeIdx];

  // Stage bio reveal after photo/contacts
  const [showBio, setShowBio] = useState(true);
  useEffect(() => {
    // when person changes, hide bio briefly, then reveal
    setShowBio(false);
    const t = setTimeout(() => setShowBio(true), 300); // delay matches CSS timing
    return () => clearTimeout(t);
  }, [activeIdx]);

  // Keyboard navigation
  const onKey = useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIdx((i) => (i + 1) % team.length);
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIdx((i) => (i - 1 + team.length) % team.length);
      }
    },
    [team.length]
  );

  useEffect(() => {
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onKey]);

  return (
    <section className="Info" id="main">
      <section className="Info__layout">
        {/* LEFT: selector list inside its OWN panel */}
        <aside className="Info__selectorPanel">
          <ul className="Info__list" role="tablist" aria-orientation="vertical">
            {team.map((m, i) => {
              const isActive = i === activeIdx;
              return (
                <li key={m.name} className="Info__listItem">
                  <button
                    className={`Info__selector ${isActive ? "is-active" : ""}`}
                    onClick={() => setActiveIdx(i)}
                    role="tab"
                    aria-selected={isActive}
                  >
                    <img
                      src={m.headshot}
                      alt=""
                      className="Info__selectorThumb"
                      loading="lazy"
                      width="64"
                      height="64"
                    />
                    <span className="Info__selectorText">
                      <span className="Info__selectorName">{m.name}</span>
                      <span className="Info__selectorRole">{m.role}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </aside>

        {/* RIGHT: spotlight card with photo+contacts (left) and bio (right) */}
        <article
          key={active.name} /* remounts on change for clean animation */
          className="Info__spotlight Info__spotlight--split is-entering"
          role="tabpanel"
          aria-live="polite"
        >
          {/* LEFT column: Photo + Contacts (auto-animate in CSS) */}
          <div className="Info__left">
            <div className="Info__photoWrap Info__inPhoto">
              <img
                src={active.headshot}
                alt={`${active.name} — ${active.role}`}
                className="Info__photo"
                loading="lazy"
              />
              <div className="Info__photoFrame" />
            </div>

            <div className="Info__contactBlock Info__contactBlock--tight Info__inContacts">
              <div className="Info__contactRow">
                <span className="Info__label">Phone</span>
                <a className="Info__value" href={`tel:${active.phone}`}>
                  {active.phone}
                </a>
              </div>
              <div className="Info__contactRow">
                <span className="Info__label">Email</span>
                <a className="Info__value" href={`mailto:${active.email}`}>
                  {active.email}
                </a>
              </div>
              {!!active.links?.length && (
                <div className="Info__links">
                  {active.links.map((l) => (
                    <a
                      key={l.label}
                      className="Info__linkPill"
                      href={l.url}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {l.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* RIGHT column: Name + Role + Bio (bio staged) */}
          <div className="Info__right">
            <h2 className="Info__name Info__inTextA">{active.name}</h2>
            <p className="Info__roleSmall Info__inTextB">{active.role}</p>

            <div className={`Info__about ${showBio ? "is-visible" : ""}`}>
              {active.about.map((p, idx) => (
                <p key={idx} className="Info__aboutP">
                  {p}
                </p>
              ))}
            </div>
          </div>
        </article>
      </section>
    </section>
  );
}

export default Info;
